import { IActionContext } from '../helpers/ActionContext';
import { L10n } from '../L10n/L10n';
import { IWorkbookBase } from '../models/IWorkbookInfo';
import { IErrorItem } from '../models/MultipleItemResult';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { Workbooks } from '../resources/Workbooks';
import { IResult } from '../ServerApi/types/IResponse';
import { IUserName } from '../ServerApi/types/IUser';
import { ActionHelpers } from './ActionHelpers';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { IConfirmChangeWorkbookOwnerScope } from './ContentActions';
import { HelpService } from './HelpService';
import { SearchSuggestionsService } from './SearchSuggestionsService';
import { WorkbookErrors } from './WorkbookErrors';

export interface IResultWithTargetOwner extends IResult {
  ownerId: string;
}

export class ChangeWorkbookOwnerAction {

  public static $inject = [
    'WorkbookErrors',
    'ActionHelpers',
    'ConfirmActionDialog',
    '$translate',
    'Workbooks',
    'SearchSuggestionsService',
    'HelpService'
  ];

  constructor(
    private WorkbookErrors: WorkbookErrors,
    private ActionHelpers: ActionHelpers,
    private ConfirmActionDialog: ConfirmActionDialog,
    private $translate: ng.translate.ITranslateService,
    private Workbooks: Workbooks,
    private suggestions: SearchSuggestionsService,
    private HelpService: HelpService) { }

  public confirmChangeOwner(workbooks: Set<IWorkbookBase>, context: IActionContext): ng.IPromise<IResultWithTargetOwner> {

    const promptWithHelpLink = () => {
      return {
        localizedStatementKey: 'changeOwnerWorkbookPromptDescription',
        links: [{
          key: '{LEARN_MORE_CHANGE_OWNER}',
          localizedValueKey: 'changeOwnerPromptLearnMore',
          url: this.HelpService.getServerHelpUrlByCategory('contentowner')
        }]
      };
    };

    const ownerPicker = {
      prompt: workbooks.size() === 1
          ? this.$translate.instant('changeOwnerWorkbookPrompt', { NAME: L10n.elideIfLong(workbooks.first().name) })
          : this.$translate.instant('changeOwnerWorkbooksPrompt', { COUNT: workbooks.size() }, L10n.Interpolations.Count),
      promptDescriptionLinkifierModel: promptWithHelpLink,
      ownerQuery: (matchText: string) => this.suggestions.usersWithoutGuest(matchText),
      targetOwner: <IUserName>null
    };

    const newDialogScope = <IConfirmChangeWorkbookOwnerScope> context.scope.$new();
    newDialogScope.ownerPicker = ownerPicker;

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: newDialogScope,
      title: this.$translate.instant('changeOwner'),
      bodyTemplateUrl: 'changeOwnerDialog.html',
      actionName: this.$translate.instant('changeOwnerConfirm', { COUNT: workbooks.size() }, L10n.Interpolations.Count),

      action: (dialogInstance: IConfirmActionDialogInstance) => {
        return this.Workbooks.setOwner(workbooks.keys(), ownerPicker.targetOwner.id)
          .then((result: IResult) => this.notifyResult(workbooks, result, ownerPicker.targetOwner.displayName));
      },
      confirmDisabled: () => !ownerPicker.targetOwner || !ownerPicker.targetOwner.id
    });

    return dialogInstance.result.then((result: IResultWithTargetOwner) => {
      return {
        ...result,
        ownerId: newDialogScope.ownerPicker.targetOwner.id
      };
    }).finally(() => newDialogScope.$destroy());
  }

  private notifyResult(workbooks: Set<IWorkbookBase>, result: IResult, ownerName: string) {
    this.ActionHelpers.notifyStandardError(
      workbooks,
      {
        successId: 'changeOwnerWorkbooksSuccess',
        errorId: 'changeOwnerWorkbooksError'
      },
      (errorItem: IErrorItem<IWorkbookBase>) => this.WorkbookErrors.errorDetailsFor(errorItem),
      { OWNER_NAME: ownerName }
    )(result);
  }

}

Module.instance().service('ChangeWorkbookOwnerAction', ChangeWorkbookOwnerAction);
